// web-frontend/src/contexts/ThemeContext.jsx
/**
 * 🎨 ThemeContext - DSK 테마 관리 시스템
 * 
 * 기능:
 * - 라이트/다크 모드 전환
 * - 시스템 설정 자동 감지
 * - localStorage 저장으로 사용자 선택 유지
 * - CSS 변수 자동 업데이트
 * 
 * 🔥 2025-12-29 수정: 
 * - 구문 오류 수정 (classList.add 백틱 문제)
 * - appStore와 클래스명 통일 (theme-light/theme-dark 사용)
 * - 테마 적용 로직 강화
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import logger from '../shared/utils/logger';
// 🔥 [2026-05-17] 멀티테넌트: tenant.brand 에서 색상 동적 로드
//   DSK2027 등 새 테넌트는 tenant.js 의 brand 객체 추가만으로 적용됨.
import { getBrand } from '../shared/config/tenant';

// ====================================
// 테마 타입 정의
// ====================================
export const ThemeTypes = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system',
};

// ====================================
// 🎨 활성 테넌트 브랜드 컬러 (런타임 hostname 기반)
//   DSK_COLORS 는 deprecated alias — 호환성을 위해 유지.
//   신규 코드는 getBrand() / getBrandColor() / CSS var(--color-primary) 사용 권장.
//   상세: docs/design.md
// ====================================
const _brand = getBrand();

export const DSK_COLORS = {
  // 'pink' alias -> tenant.brand.tertiary (DSK2027 default: #ff3875)
  pink: {
    main: _brand.tertiary || _brand.secondary,
    light: _brand.tertiaryLight || _brand.secondaryLight,
    dark: _brand.tertiaryDark || _brand.secondaryDark,
    glow: _brand.tertiaryGlow || _brand.secondaryGlow,
    pale: _brand.tertiaryPale || _brand.secondaryPale,
  },
  // 'blue' alias -> tenant.brand.primary (DSK2027 default: #052243)
  blue: {
    main: _brand.primary,
    light: _brand.primaryLight,
    dark: _brand.primaryDark,
    glow: _brand.primaryGlow,
    pale: _brand.primaryPale,
  },
  softSky: _brand.softSky,
};

// ====================================
// 라이트 테마 CSS 변수
// ====================================
const lightThemeVars = {
  // Background Colors
  '--color-bg-primary': '#F0F4FA',           // DSK2027 Soft Sky
  '--color-bg-secondary': '#FFFFFF',
  '--color-bg-tertiary': '#F1F5F9',
  '--color-bg-card': '#FFFFFF',
  '--color-bg-overlay': 'rgba(15, 23, 42, 0.5)',
  
  // Brand Colors
  '--color-primary': '#052243',              // DSK2027 Navy
  '--color-primary-light': '#1A3A5E',
  '--color-primary-dark': '#031530',
  '--color-primary-glow': 'rgba(5, 34, 67, 0.18)',
  '--color-primary-pale': 'rgba(5, 34, 67, 0.08)',
  
  '--color-secondary': '#fbba21',            // DSK2027 Orange
  '--color-secondary-light': '#FCC94D',
  '--color-secondary-dark': '#C99818',
  '--color-secondary-glow': 'rgba(251, 186, 33, 0.22)',
  '--color-secondary-pale': 'rgba(251, 186, 33, 0.10)',

  '--color-tertiary': '#ff3875',             // DSK2027 Pink Accent
  '--color-tertiary-light': '#FF6395',
  '--color-tertiary-dark': '#CC2D5E',
  '--color-tertiary-glow': 'rgba(255, 56, 117, 0.18)',
  '--color-tertiary-pale': 'rgba(255, 56, 117, 0.08)',
  
  // Text Colors
  '--color-text-primary': '#0F172A',
  '--color-text-secondary': '#475569',
  '--color-text-tertiary': '#94A3B8',
  '--color-text-inverse': '#FFFFFF',
  '--color-text-link': '#052243',
  
  // Border Colors
  '--color-border-primary': '#E2E8F0',
  '--color-border-secondary': '#CBD5E1',
  '--color-border-light': '#F1F5F9',
  '--color-border-focus': '#052243',
  '--color-border-hover': '#94A3B8',
  
  // Status Colors
  '--color-status-success': '#10B981',
  '--color-status-success-bg': 'rgba(16, 185, 129, 0.1)',
  '--color-status-warning': '#F59E0B',
  '--color-status-warning-bg': 'rgba(245, 158, 11, 0.1)',
  '--color-status-error': '#EF4444',
  '--color-status-error-bg': 'rgba(239, 68, 68, 0.1)',
  '--color-status-info': '#1A3A5E',
  '--color-status-info-bg': 'rgba(79, 123, 184, 0.1)',
  
  // Gray Scale
  '--color-gray-50': '#F8FAFC',
  '--color-gray-100': '#F1F5F9',
  '--color-gray-200': '#E2E8F0',
  '--color-gray-300': '#CBD5E1',
  '--color-gray-400': '#94A3B8',
  '--color-gray-500': '#64748B',
  '--color-gray-600': '#475569',
  '--color-gray-700': '#334155',
  '--color-gray-800': '#1E293B',
  '--color-gray-900': '#0F172A',
  
  // Shadows (Light Mode)
  '--shadow-sm': '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  '--shadow-md': '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  '--shadow-lg': '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
  '--shadow-xl': '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
  '--shadow-glow-primary': '0 0 16px rgba(5, 34, 67, 0.18)',
  '--shadow-glow-secondary': '0 0 16px rgba(251, 186, 33, 0.22)',
  '--shadow-glow-tertiary': '0 0 16px rgba(255, 56, 117, 0.18)',
  
  // Card Shadows
  '--shadow-card': '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
  '--shadow-card-hover': '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
};

// ====================================
// 다크 테마 CSS 변수
// ====================================
const darkThemeVars = {
  // Background Colors
  '--color-bg-primary': '#0F172A',
  '--color-bg-secondary': '#1E293B',
  '--color-bg-tertiary': '#334155',
  '--color-bg-card': '#1E293B',
  '--color-bg-overlay': 'rgba(15, 23, 42, 0.8)',
  
  // Brand Colors (약간 밝게 조정)
  '--color-primary': '#4F7BB8',
  '--color-primary-light': '#7EA0CC',
  '--color-primary-dark': '#052243',
  '--color-primary-glow': 'rgba(79, 123, 184, 0.18)',
  '--color-primary-pale': 'rgba(79, 123, 184, 0.10)',
  
  '--color-secondary': '#FCC94D',
  '--color-secondary-light': '#FFD873',
  '--color-secondary-dark': '#fbba21',
  '--color-secondary-glow': 'rgba(252, 201, 77, 0.22)',
  '--color-secondary-pale': 'rgba(252, 201, 77, 0.10)',

  '--color-tertiary': '#FF6395',
  '--color-tertiary-light': '#FF8AAF',
  '--color-tertiary-dark': '#ff3875',
  '--color-tertiary-glow': 'rgba(255, 99, 149, 0.18)',
  '--color-tertiary-pale': 'rgba(255, 99, 149, 0.08)',
  
  // Text Colors
  '--color-text-primary': '#F8FAFC',
  '--color-text-secondary': '#CBD5E1',
  '--color-text-tertiary': '#94A3B8',
  '--color-text-inverse': '#0F172A',
  '--color-text-link': '#FCC94D',
  
  // Border Colors
  '--color-border-primary': '#334155',
  '--color-border-secondary': '#475569',
  '--color-border-light': '#1E293B',
  '--color-border-focus': '#FCC94D',
  '--color-border-hover': '#475569',
  
  // Status Colors
  '--color-status-success': '#10B981',
  '--color-status-success-bg': 'rgba(16, 185, 129, 0.15)',
  '--color-status-warning': '#F59E0B',
  '--color-status-warning-bg': 'rgba(245, 158, 11, 0.15)',
  '--color-status-error': '#EF4444',
  '--color-status-error-bg': 'rgba(239, 68, 68, 0.15)',
  '--color-status-info': '#7EA0CC',
  '--color-status-info-bg': 'rgba(79, 123, 184, 0.15)',
  
  // Gray Scale
  '--color-gray-50': '#F8FAFC',
  '--color-gray-100': '#F1F5F9',
  '--color-gray-200': '#E2E8F0',
  '--color-gray-300': '#CBD5E1',
  '--color-gray-400': '#94A3B8',
  '--color-gray-500': '#64748B',
  '--color-gray-600': '#475569',
  '--color-gray-700': '#334155',
  '--color-gray-800': '#1E293B',
  '--color-gray-900': '#0F172A',
  
  // Shadows (Dark Mode)
  '--shadow-sm': '0 1px 2px 0 rgba(0, 0, 0, 0.3)',
  '--shadow-md': '0 4px 6px -1px rgba(0, 0, 0, 0.4)',
  '--shadow-lg': '0 10px 15px -3px rgba(0, 0, 0, 0.5)',
  '--shadow-xl': '0 20px 25px -5px rgba(0, 0, 0, 0.6)',
  '--shadow-glow-primary': '0 0 16px rgba(79, 123, 184, 0.18)',
  '--shadow-glow-secondary': '0 0 16px rgba(252, 201, 77, 0.22)',
  '--shadow-glow-tertiary': '0 0 16px rgba(255, 99, 149, 0.18)',
  
  // Card Shadows
  '--shadow-card': '0 1px 3px rgba(0, 0, 0, 0.3)',
  '--shadow-card-hover': '0 10px 15px -3px rgba(0, 0, 0, 0.4)',
};

// ====================================
// Context 생성
// ====================================
const ThemeContext = createContext(null);

// ====================================
// Storage Key
// ====================================
const THEME_STORAGE_KEY = 'dsk-theme-preference';

// ====================================
// ThemeProvider 컴포넌트
// ====================================
export const ThemeProvider = ({ children }) => {
  // 저장된 테마 설정 또는 기본값 'light' 사용
  const [themeMode, setThemeMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(THEME_STORAGE_KEY);
      return saved || ThemeTypes.LIGHT;
    }
    return ThemeTypes.LIGHT;
  });
  
  // 실제 적용되는 테마 (system인 경우 시스템 설정 따름)
  const [resolvedTheme, setResolvedTheme] = useState(ThemeTypes.LIGHT);
  
  // 시스템 다크모드 감지
  const getSystemTheme = useCallback(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
        ? ThemeTypes.DARK
        : ThemeTypes.LIGHT;
    }
    return ThemeTypes.LIGHT;
  }, []);
  
  // 🔥🔥🔥 [핵심 수정] CSS 변수 및 클래스 적용 - 구문 오류 수정 + 클래스명 통일
  // 🎨 [2026-05-17] 멀티테넌트: tenant.brand 의 값으로 brand 컬러 CSS 변수 덮어쓰기
  const applyThemeVars = useCallback((theme) => {
    const baseVars = theme === ThemeTypes.DARK ? darkThemeVars : lightThemeVars;
    const brand = getBrand();
    const root = document.documentElement;

    // 1) 기본 테마 토큰 (그레이/배경/텍스트/상태색 등) 적용
    Object.entries(baseVars).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });

    // 2) 테넌트 브랜드 컬러 오버라이드 (--color-primary / --color-secondary / --color-tertiary)
    const isDark = theme === ThemeTypes.DARK;
    const tenantBrandVars = {
      '--color-primary':         isDark ? (brand.primaryDarkMode || brand.primary) : brand.primary,
      '--color-primary-light':   brand.primaryLight,
      '--color-primary-dark':    brand.primaryDark,
      '--color-primary-glow':    brand.primaryGlow,
      '--color-primary-pale':    brand.primaryPale,
      '--color-secondary':       isDark ? (brand.secondaryDarkMode || brand.secondary) : brand.secondary,
      '--color-secondary-light': brand.secondaryLight,
      '--color-secondary-dark':  brand.secondaryDark,
      '--color-secondary-glow':  brand.secondaryGlow,
      '--color-secondary-pale':  brand.secondaryPale,
      // 🎨 [2026-05-22] DSK 2027 3색 시스템 — Tertiary (Accent) 신규
      // tenant.brand.tertiary 없으면 (DSK 2026 등 2색 시스템) secondary 와 동일값으로 fallback
      '--color-tertiary':         isDark ? (brand.tertiaryDarkMode || brand.tertiary || brand.secondary) : (brand.tertiary || brand.secondary),
      '--color-tertiary-light':   brand.tertiaryLight   || brand.secondaryLight,
      '--color-tertiary-dark':    brand.tertiaryDark    || brand.secondaryDark,
      '--color-tertiary-glow':    brand.tertiaryGlow    || brand.secondaryGlow,
      '--color-tertiary-pale':    brand.tertiaryPale    || brand.secondaryPale,
      '--color-soft-sky':         brand.softSky,
      '--color-bg-primary':       brand.softSky,
      '--color-border-focus':     brand.primary,
      '--color-text-link':        brand.primary,
      '--shadow-glow-primary':    `0 0 16px ${brand.primaryGlow || brand.primaryPale}`,
      '--shadow-glow-secondary':  `0 0 16px ${brand.secondaryGlow || brand.secondaryPale}`,
      '--shadow-glow-tertiary':   `0 0 16px ${brand.tertiaryGlow || brand.tertiaryPale || brand.secondaryGlow}`,
      '--app-splash-bg':          brand.softSky,
      '--app-splash-glow-primary': brand.splashPrimaryGlow || brand.primaryPale || brand.primaryGlow,
      '--app-splash-glow-secondary': brand.splashSecondaryGlow || brand.secondaryPale || brand.secondaryGlow,
      '--app-splash-glow-tertiary': brand.splashTertiaryGlow || brand.tertiaryPale || brand.tertiaryGlow,
      '--app-splash-glow-strong': brand.splashSecondaryGlow || brand.secondaryPale || brand.secondaryGlow,
      '--app-splash-logo-shadow': brand.splashLogoShadow || brand.splashPrimaryGlow || brand.primaryPale,
      // 호환 alias (기존 코드가 참조)
      '--tenant-brand-primary':   brand.primary,
      '--tenant-brand-secondary': brand.secondary,
      '--tenant-brand-tertiary':  brand.tertiary || brand.secondary,
    };
    Object.entries(tenantBrandVars).forEach(([key, value]) => {
      if (value) root.style.setProperty(key, value);
    });
    
    // 🔥 [수정] body 클래스 적용 - 구문 오류 수정
    // 기존: document.body.classList.add`theme-${theme}`); // ❌ 오류
    // 수정: document.body.classList.add(`theme-${theme}`); // ✅ 정상
    document.body.classList.remove('theme-light', 'theme-dark', 'light-mode', 'dark-mode');
    document.body.classList.add(`theme-${theme}`);
    
    // 🔥 [추가] appStore와 호환을 위한 추가 클래스
    if (theme === ThemeTypes.DARK) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.add('light-mode');
    }
    
    // data-theme 속성 추가 (CSS 선택자용)
    document.documentElement.setAttribute('data-theme', theme);
    
    logger.debug(`🎨 [ThemeContext] 테마 적용 완료: ${theme}`);
  }, []);
  
  // 테마 변경 핸들러
  const setTheme = useCallback((mode) => {
    setThemeMode(mode);
    localStorage.setItem(THEME_STORAGE_KEY, mode);
    logger.debug(`🎨 [ThemeContext] 테마 모드 저장: ${mode}`);
  }, []);
  
  // 테마 토글 (light <-> dark)
  const toggleTheme = useCallback(() => {
    const newTheme = resolvedTheme === ThemeTypes.DARK 
      ? ThemeTypes.LIGHT 
      : ThemeTypes.DARK;
    setTheme(newTheme);
    logger.debug(`🎨 [ThemeContext] 테마 토글: ${resolvedTheme} -> ${newTheme}`);
  }, [resolvedTheme, setTheme]);
  
  // 테마 모드 변경 시 resolved 테마 업데이트
  useEffect(() => {
    let resolved;
    
    if (themeMode === ThemeTypes.SYSTEM) {
      resolved = getSystemTheme();
    } else {
      resolved = themeMode;
    }
    
    setResolvedTheme(resolved);
    applyThemeVars(resolved);
  }, [themeMode, getSystemTheme, applyThemeVars]);
  
  // 시스템 테마 변경 감지
  useEffect(() => {
    if (themeMode !== ThemeTypes.SYSTEM) return;
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      const newTheme = e.matches ? ThemeTypes.DARK : ThemeTypes.LIGHT;
      setResolvedTheme(newTheme);
      applyThemeVars(newTheme);
      logger.debug(`🎨 [ThemeContext] 시스템 테마 변경 감지: ${newTheme}`);
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [themeMode, applyThemeVars]);
  
  // 🔥 [추가] 초기 마운트 시 테마 즉시 적용 (깜빡임 방지)
  useEffect(() => {
    const initialTheme = themeMode === ThemeTypes.SYSTEM ? getSystemTheme() : themeMode;
    applyThemeVars(initialTheme);
    logger.debug(`🎨 [ThemeContext] 초기 테마 적용: ${initialTheme}`);
  }, []); // 빈 의존성 - 마운트 시 1회만 실행
  
  // Context 값
  const contextValue = {
    // 현재 테마 모드 (light, dark, system)
    themeMode,
    // 실제 적용된 테마 (light, dark)
    theme: resolvedTheme,
    // 다크모드 여부
    isDark: resolvedTheme === ThemeTypes.DARK,
    // 테마 설정 함수
    setTheme,
    // 테마 토글 함수
    toggleTheme,
    // DSK 브랜드 컬러
    colors: DSK_COLORS,
    // 테마 타입 상수
    ThemeTypes,
  };
  
  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

// ====================================
// useTheme Hook
// ====================================
export const useTheme = () => {
  const context = useContext(ThemeContext);
  
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  
  return context;
};

// ====================================
// Default Export
// ====================================
export default ThemeContext;
